import * as React from "react";
import { cn } from "../../lib/cn";
import { ComposerChipItem } from "./ChipItem";
import type { ComposerChip } from "./types";

/**
 * Composer.ChipBar — horizontal row of dismissible capability chips.
 *
 * Two layout modes:
 *
 *   - **Inline (default)** — sits inside the composer card between the
 *     RichEditor and the bottom Bar, eating ~32px of vertical real
 *     estate. Backward-compat with v1 surfaces.
 *
 *   - **Floating** (``floating={true}``, June 11, 2026) — chip strip
 *     escapes the composer card and floats glass-morphism style HALF
 *     ABOVE the top border of the card (``-translate-y-1/2``). No
 *     vertical space is taken from the textarea / bottom bar, so the
 *     idle composer collapses to its minimal height while active
 *     capabilities stay visually anchored to the input they affect.
 *     Pattern: Raycast active-filter pill, Granola call-notes badge,
 *     Linear smart-filter chip — all SOTA peers (June 2026) that
 *     surface "in-flight state" without reserving layout.
 *
 *     The caller MUST mount this inside a ``position: relative``
 *     ancestor (``Composer.Root`` already satisfies this). The strip
 *     is positioned ``absolute`` and ``z-10`` so it sits above the
 *     composer chrome but below any popover. ``pointer-events: auto``
 *     keeps the dismiss X clickable through the floating layer.
 *
 * Renders `chips` left-aligned. Children can be appended (e.g. the inline
 * `+` AttachButton lives inside this row in the v18 mockup, to the right of
 * the chips). Each chip animates in via `animate-chip-in` with a 60ms stagger.
 *
 * Empty + no children → renders nothing (avoids a stray flex gap).
 */
export interface ComposerChipBarProps {
  /** Currently active capability chips. */
  chips?: ComposerChip[];
  /** Click on chip body — for clickable chips only. */
  onChipClick?: (id: string) => void;
  /** Click on a chip X — caller removes the chip from state. */
  onChipDismiss?: (id: string) => void;
  /** Anything to render after the chips (typically `<Composer.AttachButton/>`). */
  children?: React.ReactNode;
  /** Stagger between adjacent chips, ms. Default `60`. */
  staggerMs?: number;
  /**
   * Lift the chip strip out of the layout flow into a glass-morphism
   * pill floating half above the composer top border. The container
   * MUST be ``position: relative`` (``Composer.Root`` already is).
   * See the file-level docstring for the SOTA rationale.
   */
  floating?: boolean;
  className?: string;
}

export const ComposerChipBar = React.forwardRef<HTMLDivElement, ComposerChipBarProps>(
  (
    { chips, onChipClick, onChipDismiss, children, staggerMs = 60, floating, className },
    ref
  ) => {
    const hasChips = (chips?.length ?? 0) > 0;
    if (!hasChips && !children) return null;
    if (floating) {
      return (
        <div
          ref={ref}
          // Floating chip strip — half-overlaps the composer top
          // border so the affordance lives at the boundary between
          // chat history (above) and input (below). z-10 sits above
          // composer chrome; pointer-events-auto keeps the dismiss X
          // clickable through the floating layer.
          //
          // June 11, 2026 — outer chrome STRIPPED. I chip stessi sono
          // già pill rounded-full con bg + border + dot tone, quindi
          // il wrapper container con altro bg/border/shadow/blur era
          // pill-dentro-pill (UX review: "pesante"). Pattern Raycast/
          // Linear/Granola: chip individuali floating naked, gap fra
          // loro. Risultato: 50% meno chrome verticale, il composer
          // respira sotto.
          className={cn(
            "absolute -top-2 left-3 z-10 flex flex-wrap items-center gap-1.5 -translate-y-1/2",
            "pointer-events-auto",
            className,
          )}
        >
          {chips?.map((c, i) => (
            <ComposerChipItem
              key={c.id}
              chip={c}
              onClick={onChipClick}
              onDismiss={onChipDismiss}
              delayMs={i * staggerMs}
            />
          ))}
          {children}
        </div>
      );
    }
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-wrap items-center gap-1 px-2.5",
          // Vertical rhythm matches v18 `.active-mode-chips` (sits between
          // textarea and bottom bar — only chip-bar padding, no bottom).
          "py-1",
          className
        )}
      >
        {chips?.map((c, i) => (
          <ComposerChipItem
            key={c.id}
            chip={c}
            onClick={onChipClick}
            onDismiss={onChipDismiss}
            delayMs={i * staggerMs}
          />
        ))}
        {children}
      </div>
    );
  }
);
ComposerChipBar.displayName = "Composer.ChipBar";
